// RecordView: record lines to build a script without a PDF (React version)

import { useState, useRef } from 'react';
import { useAppDispatch } from '../store';
import { setParsedLines, setScriptMeta, setRecordedAudio } from '../store/scriptSlice';
import { saveAudioBlob, deleteAudioBlob, createAudioId } from '../lib/audio-storage';
import type { RecordedLine } from '../lib/scriptParser';
import { ensureSessionMicStream, getSpeechRecognition } from '../services/mic-stt';
import { parseScript } from '../services/scriptParser';
import type { ViewName } from '../app';

interface RecordViewProps {
    onNavigate: (view: ViewName) => void;
}

export function RecordView({ onNavigate }: RecordViewProps) {
    const dispatch = useAppDispatch();

    const [recordedCharacters, setRecordedCharacters] = useState<string[]>([]);
    const [recordedLines, setRecordedLines] = useState<RecordedLine[]>([]);
    const [isRecordingLine, setIsRecordingLine] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [selectedChar, setSelectedChar] = useState('');

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recognitionRef = useRef<any>(null);
    const transcriptRef = useRef({ final: '', interim: '' });

    function addCharacter() {
        const name = prompt('Enter label (e.g., JESSE):');
        if (!name?.trim()) return;
        const upper = name.trim().toUpperCase();
        if (recordedCharacters.includes(upper)) { alert('Label already exists!'); return; }
        setRecordedCharacters([...recordedCharacters, upper]);
    }

    function removeCharacter(name: string) {
        setRecordedCharacters(recordedCharacters.filter(c => c !== name));
        setRecordedLines(recordedLines.filter(l => l.character !== name));
    }

    function removeRecordedLine(index: number) {
        const removed = recordedLines[index];
        if (removed?.audioId) deleteAudioBlob(removed.audioId).catch(() => {});
        setRecordedLines(recordedLines.filter((_, i) => i !== index));
    }

    function clearAll() {
        if (recordedLines.length === 0) return;
        if (!confirm('Clear all recorded lines?')) return;
        recordedLines.forEach(l => { if (l.audioId) deleteAudioBlob(l.audioId).catch(() => {}); });
        setRecordedLines([]);
    }

    async function startRecording() {
        const SpeechRecognition = getSpeechRecognition();
        setIsRecordingLine(true);
        setStatusText('Listening... Speak the line now.');

        if (!SpeechRecognition) {
            setStatusText('Speech recognition not supported in this browser.');
            setIsRecordingLine(false);
            return;
        }

        let stream: MediaStream | null;
        try {
            stream = await ensureSessionMicStream();
            if (!stream) throw new Error('Mic permission denied');
        } catch {
            setStatusText('Microphone access denied.');
            setIsRecordingLine(false);
            return;
        }

        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        const chunks: Blob[] = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

        recorder.onstop = async () => {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            const audioId = createAudioId();
            await saveAudioBlob(audioId, blob).catch(console.error);
            const transcript = (transcriptRef.current.final || transcriptRef.current.interim || '').trim();
            if (transcript) {
                setRecordedLines(prev => [...prev, { character: selectedChar, text: transcript, audioId }]);
                setStatusText(`Added: "${transcript.substring(0, 50)}..."`);
            } else {
                setStatusText('No speech detected. Try again.');
            }
            setIsRecordingLine(false);
            transcriptRef.current = { final: '', interim: '' };
        };

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognitionRef.current = recognition;
        transcriptRef.current = { final: '', interim: '' };

        recognition.onresult = (event: any) => {
            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    transcriptRef.current.final += event.results[i][0].transcript;
                } else {
                    interim += event.results[i][0].transcript;
                }
            }
            setStatusText(`Hearing: "${transcriptRef.current.final + interim}"`);
        };
        recognition.onerror = (event: any) => {
            setStatusText('Error: ' + event.error);
            stopRecording();
        };

        recorder.start();
        recognition.start();
    }

    function stopRecording() {
        setIsRecordingLine(false);
        try { recognitionRef.current?.stop(); } catch {}
        if (mediaRecorderRef.current?.state !== 'inactive') {
            try { mediaRecorderRef.current?.stop(); } catch {}
        }
    }

    async function processScript() {
        if (recordedLines.length === 0) return;
        let scriptText = '';
        let currentChar: string | null = null;
        for (const line of recordedLines) {
            if (line.character !== currentChar) {
                scriptText += `\n${line.character}\n`;
                currentChar = line.character;
            }
            scriptText += line.text + '\n';
        }
        dispatch(setScriptMeta({ name: 'Recorded Script', id: null }));
        dispatch(setRecordedAudio(recordedLines.map(l => ({ character: l.character, text: l.text, audioId: l.audioId }))));
        if (await parseScript(scriptText.trim(), 'Recorded Script')) {
            onNavigate('character');
        }
    }

    return (
        <div className="character-select-container" id="record-view">
            <h1 className="upload-title">Record Lines</h1>
            <p className="upload-subtitle">Record lines to build a script without a PDF.</p>

            <div className="record-characters">
                <div className="record-characters-header">
                    <span>Labels</span>
                    <button className="btn btn-small btn-secondary" onClick={addCharacter}>+ Add Label</button>
                </div>
                <div>
                    {recordedCharacters.length === 0 ? (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>No labels yet</span>
                    ) : (
                        recordedCharacters.map(name => (
                            <div key={name} className="record-character-chip">
                                {name}
                                <span className="remove-char" onClick={() => removeCharacter(name)}>×</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="record-lines-section">
                <div className="record-lines-header">
                    <span>Lines ({recordedLines.length})</span>
                    <button className="btn btn-small btn-secondary" onClick={clearAll}>Clear All</button>
                </div>
                <div className="recorded-lines-list">
                    {recordedLines.length === 0 ? (
                        <div className="empty-lines">No lines recorded yet</div>
                    ) : (
                        recordedLines.map((line, i) => (
                            <div key={i} className="recorded-line-item">
                                <span className="recorded-line-char">{line.character}</span>
                                <span className="recorded-line-text">{line.text}</span>
                                <span className="recorded-line-delete" onClick={() => removeRecordedLine(i)}>×</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="record-controls">
                <select
                    className="record-select"
                    value={selectedChar}
                    onChange={(e) => setSelectedChar(e.target.value)}
                >
                    <option value="">Select label...</option>
                    {recordedCharacters.map(name => (
                        <option key={name} value={name}>{name}</option>
                    ))}
                </select>
                <button
                    className={`btn btn-primary record-btn ${isRecordingLine ? 'recording' : ''}`}
                    disabled={recordedCharacters.length === 0}
                    onClick={() => {
                        if (!selectedChar) { alert('Please select a label first'); return; }
                        isRecordingLine ? stopRecording() : startRecording();
                    }}
                >
                    {isRecordingLine ? '⏹️ Stop Recording' : '🎙️ Record Line'}
                </button>
            </div>
            <div className="record-status">{statusText}</div>

            <div className="btn-row">
                <button className="btn btn-secondary" onClick={() => onNavigate('upload')}>Back</button>
                {recordedLines.length > 0 && (
                    <button className="btn btn-secondary" onClick={() => {/* TODO: full run-through */}}>
                        ▶ Play Full Run-Through
                    </button>
                )}
                <button className="btn btn-primary" onClick={processScript}>Use Recording</button>
            </div>
        </div>
    );
}
